import type { FC} from 'react';
import { useEffect, useState } from 'react'
import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { QuickTips } from './QuickTips'
import { AutoSaveStatus, type SaveStatus } from './AutoSaveStatus'
import { KeyboardShortcuts } from './KeyboardShortcuts'

interface HelpSidebarProps {
  activeTab: string
  saveStatus: SaveStatus
  onRetry?: () => void
  isVisible: boolean
  onToggle: () => void
  onStartTour: () => void
  hasCompletedTour?: boolean
  onResetTour?: () => void
}

/**
 * Contextual help sidebar for the Song Settings modal
 *
 * Features:
 * - Quick tips that update based on active tab
 * - Guided tour launch button
 * - Keyboard shortcuts reference
 * - Auto-save status indicator
 *
 * Responsive behavior:
 * - Desktop (≥1280px): Visible by default, 300px width
 * - Tablet/Mobile (<1280px): Hidden by default, toggle button visible
 */
export const HelpSidebar: FC<HelpSidebarProps> = ({
  activeTab,
  saveStatus,
  onRetry,
  isVisible,
  onToggle,
  onStartTour,
  hasCompletedTour,
  onResetTour
}) => {
  // Track if we're on mobile for responsive behavior
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1280)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Toggle button shown when sidebar is hidden
  if (!isVisible) {
    return (
      <button
        onClick={onToggle}
        className="fixed right-4 bottom-4 p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 z-10"
        aria-label="Show help sidebar"
        data-testid="help-sidebar-toggle"
      >
        <QuestionMarkCircleIcon className="w-6 h-6" />
      </button>
    )
  }

  return (
    <aside
      className={`
        bg-gray-50 border-l border-gray-200 flex flex-col
        ${isMobile
          ? 'fixed inset-y-0 right-0 w-80 z-20 shadow-xl animate-slideIn'
          : 'w-80 flex-shrink-0'
        }
      `}
      aria-label="Help and tips"
      data-testid="help-sidebar"
      data-tour="help-sidebar"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <QuestionMarkCircleIcon className="w-5 h-5 text-blue-600" />
          Help & Tips
        </h3>
        <button
          onClick={onToggle}
          className="p-1 text-gray-400 hover:text-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Hide help sidebar"
          data-testid="help-sidebar-close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Content - scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Quick Tips Section */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Quick Tips</h4>
          <QuickTips activeTab={activeTab} />
        </section>

        {/* Guided Tour Section */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Learn by Doing</h4>
          <button
            onClick={onStartTour}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 settings-button-lift"
            data-testid="guided-tour-button"
          >
            🎯 Take a Tour
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Learn the settings with an interactive walkthrough
          </p>
          {hasCompletedTour && onResetTour && (
            <button
              onClick={onResetTour}
              className="text-xs text-gray-500 hover:text-gray-700 mt-1 underline"
              data-testid="reset-tour-button"
            >
              Restart tour
            </button>
          )}
        </section>

        {/* Keyboard Shortcuts Section */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Keyboard Shortcuts</h4>
          <KeyboardShortcuts />
        </section>
      </div>

      {/* Footer - Auto-Save Status */}
      <div className="p-4 border-t border-gray-200 flex-shrink-0">
        <AutoSaveStatus status={saveStatus} onRetry={onRetry} />
      </div>
    </aside>
  )
}

export default HelpSidebar
